import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { AlertCircle } from "lucide-react";
import LaunchPromoBanner from "@/components/LaunchPromoBanner";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import NameSuggestions from "@/components/NameSuggestions";

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot — must stay empty (humans can't see it)
  const [error, setError] = useState("");
  // Structured NAME_TAKEN error from backend — carries { message, suggestions[] }
  const [nameError, setNameError] = useState(null);
  const [loading, setLoading] = useState(false);

  const gdprText = {
    sk: { label: <>Súhlasím so spracovaním osobných údajov podľa <Link to="/privacy" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Zásad ochrany súkromia</Link> a beriem na vedomie <Link to="/terms" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Obchodné podmienky</Link>.</>, missing: "Pred registráciou musíš odsúhlasiť spracovanie osobných údajov." },
    en: { label: <>I consent to the processing of my personal data according to the <Link to="/privacy" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Privacy Policy</Link> and accept the <Link to="/terms" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Terms of Service</Link>.</>, missing: "You must accept the privacy policy before registering." },
    cs: { label: <>Souhlasím se zpracováním osobních údajů podle <Link to="/privacy" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Zásad ochrany soukromí</Link> a beru na vědomí <Link to="/terms" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Obchodní podmínky</Link>.</>, missing: "Před registrací musíš odsouhlasit zpracování osobních údajů." },
    de: { label: <>Ich willige in die Verarbeitung meiner personenbezogenen Daten gemäß der <Link to="/privacy" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Datenschutzerklärung</Link> ein und akzeptiere die <Link to="/terms" target="_blank" className="text-gold-500 hover:text-gold-400 underline">AGB</Link>.</>, missing: "Du musst der Datenverarbeitung zustimmen, bevor du dich registrierst." },
    pl: { label: <>Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z <Link to="/privacy" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Polityką prywatności</Link> i akceptuję <Link to="/terms" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Regulamin</Link>.</>, missing: "Musisz zaakceptować politykę prywatności przed rejestracją." },
    fr: {
  label: <>J'accepte le traitement de mes données personnelles conformément à la <Link to="/privacy" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Politique de confidentialité</Link> et je prends connaissance des <Link to="/terms" target="_blank" className="text-gold-500 hover:text-gold-400 underline">Conditions générales</Link>.</>,
  missing: "Tu dois accepter le traitement des données personnelles avant de t'inscrire."
},
  }[lang] || {};

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setError(gdprText.missing);
      return;
    }
    setError(""); setNameError(null); setLoading(true);
    try {
      await register(name, email, password, consent, website);
      nav("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured NAME_TAKEN error → render suggestion pills; keep top banner empty
      if (detail && typeof detail === "object" && detail.code === "NAME_TAKEN") {
        setNameError(detail);
      } else {
        setError(typeof detail === "string" ? detail : (detail?.message || "Registration failed"));
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md border border-zinc-800 bg-[var(--wb-surface)] p-8">
        <div className="flex items-center gap-2 mb-8">
          <img src="/tigik-logo.png" alt="Tigik" className="w-10 h-10 object-cover border border-gold-600/40" />
          <span className="font-display font-black text-xl">TIGIK</span>
        </div>
        <h1 className="font-display font-bold text-3xl mb-1">{t.auth.registerTitle}</h1>
        <p className="text-sm text-zinc-500 mb-6 font-mono uppercase tracking-wider">→ {t.auth.orbital}</p>
        <div className="mb-6"><LaunchPromoBanner compact /></div>

        <GoogleLoginButton next="/" label="Registrovať cez Google" />
        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-zinc-800 flex-1" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">alebo</span>
          <div className="h-px bg-zinc-800 flex-1" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          {/* Honeypot — invisible to humans, bots auto-fill it. Server silently rejects. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 0, height: 0, overflow: "hidden", opacity: 0, pointerEvents: "none" }}>
            <label htmlFor="hp-website">Website</label>
            <input
              id="hp-website"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.auth.name}</label>
            <input
              data-testid="register-name"
              type="text" required value={name} onChange={(e) => { setName(e.target.value); if (nameError) setNameError(null); }}
              className="wb-input w-full px-3 py-2 text-sm"
            />
            <NameSuggestions
              error={nameError}
              lang={lang}
              onPick={(s) => { setName(s); setNameError(null); }}
              testid="register-name-suggestions"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.auth.email}</label>
            <input
              data-testid="register-email"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="wb-input w-full px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.auth.password}</label>
            <input
              data-testid="register-password"
              type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="wb-input w-full px-3 py-2 text-sm"
            />
          </div>
          {error && (
            <div data-testid="register-error" className="flex items-center gap-2 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> <span>{error}</span>
            </div>
          )}
          <label className="flex items-start gap-2.5 text-xs text-zinc-400 leading-relaxed cursor-pointer select-none" data-testid="register-consent-label">
            <input
              data-testid="register-consent"
              type="checkbox"
              required
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-gold-500 flex-shrink-0 cursor-pointer"
            />
            <span>{gdprText.label}</span>
          </label>
          <button
            data-testid="register-submit"
            type="submit" disabled={loading || !consent}
            className="wb-btn-primary w-full py-3 text-sm disabled:opacity-50"
          >
            {loading ? t.common.loading : t.auth.submitRegister}
          </button>
        </form>
        <div className="mt-6 text-sm text-zinc-500 text-center">
          {t.auth.hasAccount} <Link to="/login" className="text-gold-500 hover:text-gold-400" data-testid="goto-login">{t.auth.submitLogin}</Link>
        </div>
      </div>
    </div>
  );
}
